import { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FileText, Plus, Trash2, ChevronRight, Copy, Send, ChevronLeft } from 'lucide-react';
import { api } from '../lib/api.ts';
import { extractError } from '../lib/types.ts';
import { PageHeader } from '../components/ui/PageHeader.tsx';
import { Button } from '../components/ui/Button.tsx';
import { Badge } from '../components/ui/Badge.tsx';
import { Modal, ModalFooter } from '../components/ui/Modal.tsx';
import { Input } from '../components/ui/Input.tsx';
import { EmptyState } from '../components/ui/EmptyState.tsx';
import { SkeletonRows } from '../components/ui/Skeleton.tsx';
import { ConfirmDialog } from '../components/ui/ConfirmDialog.tsx';
import { useToast } from '../components/ui/Toast.tsx';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface Entry {
  id: string;
  title: string;
  slug: string;
  status: 'draft' | 'published';
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

interface Collection {
  id: string;
  name: string;
  slug: string;
}

function slugify(s: string) {
  return s
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function fmt(d: string) {
  return new Date(d).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  });
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export function CmsEntriesPage() {
  const { spaceId, colId } = useParams<{ spaceId: string; colId: string }>();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();

  const [showCreate, setShowCreate] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Entry | null>(null);
  const [title, setTitle] = useState('');
  const [slug, setSlug] = useState('');

  const BASE = `/api/v1/cms/spaces/${spaceId}/collections/${colId}`;

  const { data: collection } = useQuery<Collection>({
    queryKey: ['cms-collection', spaceId, colId],
    queryFn: () => api.get(BASE).then((r) => r.data),
    enabled: !!spaceId && !!colId,
  });

  const { data = [], isLoading } = useQuery<Entry[]>({
    queryKey: ['cms-entries', spaceId, colId],
    queryFn: () => api.get(`${BASE}/entries`).then((r) => r.data.entries ?? r.data),
    enabled: !!spaceId && !!colId,
  });

  const createMutation = useMutation({
    mutationFn: (body: object) => api.post(`${BASE}/entries`, body),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ['cms-entries', spaceId, colId] });
      setShowCreate(false);
      setTitle('');
      setSlug('');
      toast.success('Entry created');
      navigate(`/spaces/${spaceId}/collections/${colId}/entries/${(res.data as Entry).id}`);
    },
    onError: (err) => toast.error(extractError(err, 'Failed to create entry')),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete(`${BASE}/entries/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-entries', spaceId, colId] });
      setDeleteTarget(null);
      toast.success('Entry deleted');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  const duplicateMutation = useMutation({
    mutationFn: (id: string) =>
      api.post(`${BASE}/entries/${id}/duplicate`).then((r) => r.data as Entry),
    onSuccess: (newEntry) => {
      qc.invalidateQueries({ queryKey: ['cms-entries', spaceId, colId] });
      toast.success('Entry duplicated');
      navigate(`/spaces/${spaceId}/collections/${colId}/entries/${newEntry.id}`);
    },
    onError: (err) => toast.error(extractError(err, 'Failed to duplicate entry')),
  });

  const publishMutation = useMutation({
    mutationFn: (id: string) => api.post(`${BASE}/entries/${id}/publish`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['cms-entries', spaceId, colId] });
      toast.success('Entry published');
    },
    onError: (err) => toast.error(extractError(err)),
  });

  function handleCreate() {
    if (!title.trim()) return;
    createMutation.mutate({ title: title.trim(), slug: slug.trim() || undefined });
  }

  const statusVariant: Record<string, 'success' | 'default'> = {
    published: 'success',
    draft: 'default',
  };

  return (
    <div>
      <div className="flex items-center gap-2 text-xs text-zinc-400 mb-4">
        <Link
          to={`/spaces/${spaceId}/collections`}
          className="hover:text-zinc-600 flex items-center gap-1"
        >
          <ChevronLeft size={13} /> Collections
        </Link>
        <span>/</span>
        <span className="text-zinc-700 font-medium">{collection?.name ?? 'Entries'}</span>
      </div>

      <PageHeader
        title={collection ? `${collection.name} — Entries` : 'Entries'}
        description="Manage content entries for this collection"
        action={
          <Button onClick={() => setShowCreate(true)} size="sm">
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New Entry
          </Button>
        }
      />

      {isLoading ? (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <SkeletonRows count={4} cols={5} />
        </div>
      ) : data.length === 0 ? (
        <EmptyState
          icon={<FileText size={26} />}
          title="No entries yet"
          description="Create your first entry for this collection"
          action={{ label: 'New Entry', onClick: () => setShowCreate(true) }}
        />
      ) : (
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-100">
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Slug
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-zinc-400 uppercase tracking-wider">
                  Updated
                </th>
                <th className="px-4 py-3 w-28" />
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {data.map((entry) => (
                <tr
                  key={entry.id}
                  className="hover:bg-zinc-50 transition-colors group cursor-pointer"
                  onClick={() =>
                    navigate(`/spaces/${spaceId}/collections/${colId}/entries/${entry.id}`)
                  }
                >
                  <td className="px-4 py-3">
                    <div className="font-medium text-zinc-900">{entry.title}</div>
                    {entry.publishedAt && (
                      <div className="text-xs text-zinc-400 mt-0.5">
                        Published {fmt(entry.publishedAt)}
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-zinc-500 font-mono text-xs">{entry.slug}</td>
                  <td className="px-4 py-3">
                    <Badge variant={statusVariant[entry.status] ?? 'default'}>{entry.status}</Badge>
                  </td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{fmt(entry.updatedAt)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {entry.status === 'draft' && (
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            publishMutation.mutate(entry.id);
                          }}
                          className="p-1.5 text-zinc-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-md"
                          title="Publish"
                        >
                          <Send size={12} />
                        </button>
                      )}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          duplicateMutation.mutate(entry.id);
                        }}
                        className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-100 rounded-md"
                        title="Duplicate"
                      >
                        <Copy size={12} />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteTarget(entry);
                        }}
                        className="p-1.5 text-zinc-400 hover:text-red-500 hover:bg-red-50 rounded-md"
                        title="Delete"
                      >
                        <Trash2 size={12} />
                      </button>
                      <ChevronRight size={13} className="text-zinc-300" />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal
        open={showCreate}
        onClose={() => {
          setShowCreate(false);
          setTitle('');
          setSlug('');
        }}
        title="New Entry"
      >
        <div className="space-y-4">
          <Input
            label="Title *"
            value={title}
            onChange={(e) => {
              setTitle(e.target.value);
              setSlug(slugify(e.target.value));
            }}
            placeholder="My First Post"
          />
          <div>
            <Input
              label="Slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              placeholder="my-first-post"
              className="font-mono text-xs"
            />
            <p className="text-xs text-zinc-400 mt-1">Auto-generated from title if blank</p>
          </div>
        </div>
        {createMutation.isError && (
          <p className="text-xs text-red-500 mt-3">{extractError(createMutation.error)}</p>
        )}
        <ModalFooter>
          <Button variant="ghost" size="sm" onClick={() => setShowCreate(false)}>
            Cancel
          </Button>
          <Button
            loading={createMutation.isPending}
            disabled={!title.trim()}
            onClick={handleCreate}
          >
            Create Entry
          </Button>
        </ModalFooter>
      </Modal>

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete entry?"
        message={`"${deleteTarget?.title}" will be permanently deleted.`}
        confirmLabel="Delete"
        variant="danger"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
